import { useState } from 'react'
import { useMutation, useSubscription } from '@apollo/client/react'
import { tr } from 'react-hook-form-auto'
import { ImageShow, nlToTag } from '../../pintor'
import { RecipeEditor } from './RecipeEditor'
import { CommentSection } from '../threads/CommentSection'
import { amountText } from '../../models/units'
import { Meal } from '../../models/meal'
import { MealTimelineRead } from './MealTimelineRead'

import styles from './MealInfo.sass'

const renderAmount = (entry) =>
  amountText(entry.amount, entry.unit, entry.amountMax)

const renderIngredientEntry = (idx, entry) => {
  const { ingredient } = entry

  if (ingredient) {
    return (
      <tr key={idx}>
        <td>
          {ingredient.name}
        </td>
        <td className="amount">
          {renderAmount(entry)}
        </td>
      </tr>
    )
  } else
    return null
}

const getIngredientList = (meal) => meal.ingredientList || []

const renderIngredientEntries = (meal) => {
  const ingredientList = getIngredientList(meal)

  return ingredientList.map((entry, idx) => {
    return renderIngredientEntry(idx, entry)
  })
}

const renderRecipe = doc => {
  const { recipe } = doc

  if (recipe) {
    return (
      <div className="recipe-show">
        <h2>{tr('shop.recipe')}</h2>
        <RecipeEditor
          meal={doc}
          initial={JSON.parse(recipe)}
          readOnly
        />
      </div>
    )
  }
}

const timelineIngredients = doc =>
  getIngredientList(doc)
    .filter(entry => entry.ingredient && entry.ingredient.id)
    .map(entry => ({
      id: String(entry.ingredient.id),
      name: entry.ingredient.name
    }))

const applyDone = (doneIds, segmentId, done) => {
  const next = new Set(doneIds)

  if (done)
    next.add(segmentId)
  else
    next.delete(segmentId)

  return next
}

export const MealInfo = ({
  doc
}) => {
  const timeline = (doc.timeline || []).map(({ __typename, ...entry }) => entry)
  const ingredients = timelineIngredients(doc)

  const [ doneIds, setDoneIds ] = useState(new Set())
  const [ toggleDone ] = useMutation(Meal.timelineDone)

  useSubscription(Meal.timelineDoneSub, {
    variables: { id: parseInt(doc.id) },
    onData: ({ data }) => {
      const event = data && data.data && data.data.mealTimelineDone
      if (event)
        setDoneIds(prev => applyDone(prev, event.segmentId, event.done))
    }
  })

  const isAuthor = doc.user == null

  const handleToggleDone = segmentId => {
    const done = doneIds.has(segmentId)

    setDoneIds(prev => applyDone(prev, segmentId, !done))

    if (isAuthor) {
      toggleDone({
        variables: { id: parseInt(doc.id), segmentId, done: !done }
      })
    }
  }

  return (
    <div className={styles.mealIngredients}>
      <h2>{doc.name}</h2>
      <div className={styles.mealNotes}>
        {nlToTag(doc.memo, 'div', { className: styles.memoLine })}
      </div>
      <div className={styles.mealPicIngredients}>
        {
          doc && doc.images && doc.images.length > 0 &&
            <div className={styles.mealShow}>
              <ImageShow
                file={doc.images[0]}
                side={240}
              />
            </div>
        }
        <div className={styles.mealShow}>
          <table className="table table-condensed">
            <tbody>
              {renderIngredientEntries(doc)}
            </tbody>
          </table>
        </div>
      </div>
      {timeline.length > 0 &&
        <div className={styles.mealTimeline}>
          <h2>{tr('models.meal.timeline._field')}</h2>
          <MealTimelineRead
            timeline={timeline}
            ingredients={ingredients}
            doneIds={doneIds}
            onToggleDone={handleToggleDone}
          />
        </div>}
      {renderRecipe(doc)}
      { /* <MealNutriments meal={doc} /> */ }
      <CommentSection parentType="meals" parentId={doc.id} />
    </div>
  )
}
